import Vue from 'vue'
import Nes from 'nes/client'
import { createCrapsClient, initialCrapsState } from '../lib/craps-client'

let client
let interval
let wake
let closeSocket

function request (path, options = {}) {
  const controller = new window.AbortController()
  const timeout = setTimeout(() => controller.abort(), 8000)
  const headers = { Accept: 'application/json' }
  if (options.body) {
    headers['Content-Type'] = 'application/json'
    headers['x-craps-csrf'] = options.csrfToken || ''
  }
  return window.fetch(Vue.config.CURRENCY_SERVICE_BASE_URL.replace(/\/$/, '') + '/craps' + path, {
    method: options.method || 'GET',
    credentials: options.private ? 'include' : 'omit',
    cache: 'no-store',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: controller.signal
  }).then(async response => {
    if (!response.ok) {
      const error = new Error('Craps request failed')
      error.status = response.status
      try {
        const result = await response.json()
        if (result && result.error) {
          if (typeof result.error.code === 'string') error.code = result.error.code
          if (typeof result.error.retryable === 'boolean') error.retryable = result.error.retryable
        }
      } catch (_) {}
      throw error
    }
    return response.json()
  }).then(result => { clearTimeout(timeout); return result }, error => { clearTimeout(timeout); throw error })
}

function subscribe (handler, status) {
  return new Promise((resolve, reject) => {
    let disposed = false
    let socket
    let retryTimer
    let failed = false
    closeSocket = () => { disposed = true; clearTimeout(retryTimer); if (socket) socket.disconnect() }
    const close = closeSocket
    function connect () {
      if (disposed) return
      socket = new Nes.Client(Vue.config.WS_SERVICE_BASE_URL)
      let subscribed = false
      socket.onError = () => { if (!disposed) status('stale') }
      socket.onDisconnect = () => { if (!disposed) status('stale') }
      socket.onConnect = () => { if (!disposed && subscribed) status('live') }
      function retry (error) {
        socket.disconnect()
        if (disposed) return
        failed = true
        reject(error)
        retryTimer = setTimeout(connect, 3000)
      }
      socket.connect({ timeout: 8000 }, error => {
        if (disposed) return reject(new Error('Subscription stopped'))
        if (error) return retry(error)
        socket.subscribe('/craps/table', handler, error => {
          if (error) return retry(error)
          subscribed = true
          if (failed) status('live')
          resolve(close)
        })
      })
    }
    connect()
  })
}

const initial = () => Object.assign(initialCrapsState(), { now: Date.now() })
export default {
  namespaced: true,
  state: initial(),
  mutations: {
    update (state, snapshot) { Object.keys(snapshot).forEach(key => Vue.set(state, key, snapshot[key])) },
    tick (state) { state.now = Date.now() }
  },
  actions: {
    async start (context, options = {}) {
      context.dispatch('stop')
      context.commit('update', initial())
      let storage
      try { storage = window.sessionStorage } catch (_) {}
      const active = createCrapsClient({ request, subscribe, storage, onChange: state => context.commit('update', state) })
      client = active
      if (!options.publicOnly) active.setOwner(context.rootState.currentUser && context.rootState.currentUser.id)
      wake = () => {
        if (document.hidden || client !== active) return
        active.refreshPublic()
        if (!options.publicOnly) active.refreshPrivate()
      }
      document.addEventListener('visibilitychange', wake)
      window.addEventListener('focus', wake)
      window.addEventListener('online', wake)
      let ticks = 0
      interval = setInterval(() => {
        context.commit('tick')
        if (document.hidden) return
        ticks++
        if (ticks % 5 === 0 && !options.publicOnly) active.refreshPrivate()
        // Public HTTP also detects a silently stalled subscription and deadline.
        if (ticks % 5 === 0) active.refreshPublic()
      }, 1000)
      await active.start()
      if (client === active && !options.publicOnly) active.refreshPrivate()
    },
    setOwner (context, owner) {
      if (client) { client.setOwner(owner); client.refreshPrivate() }
    },
    stop () {
      if (client) client.dispose()
      client = null
      if (closeSocket) closeSocket()
      closeSocket = null
      clearInterval(interval)
      if (wake) {
        document.removeEventListener('visibilitychange', wake)
        window.removeEventListener('focus', wake)
        window.removeEventListener('online', wake)
      }
    },
    submit (context, payload) { return client ? client.submit(payload.intent || payload, payload.endpoint) : Promise.reject(new Error('Table is not connected')) },
    retry () { return client && client.retry() },
    checkReceipt () { return client && client.checkReceipt() },
    refresh () { if (client) { client.refreshPublic(); return client.refreshPrivate() } }
  }
}



// WEBPACK FOOTER //
// ./src/store/craps.js